import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';

import { type CSSResult, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { getOpenApiSpec } from '../libraries/api_client/administration.js';
import { valueOrThrow } from '../libraries/api_result.js';
import { getAccessToken } from '../libraries/auth.js';
import { loadFailed, reloadHint } from '../libraries/error_copy.js';
import { runtimeConfig } from '../libraries/runtime_config.js';
import { getResolvedScheme } from '../libraries/theme.js';
import { isRecord } from '../libraries/type_guards/values.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';
import { swaggerDarkTheme } from './api_docs_dark_theme.js';
import { apiDocsScrollerStyles } from './api_docs_scroller_styles.js';

/**
 * The outgoing request object Swagger-UI hands to a `requestInterceptor`.
 * Only `headers` is mutated here; the index signature preserves the other
 * fields (url, method, body, …) Swagger-UI round-trips unchanged.
 */
interface ISwaggerRequest {
    headers: Record<string, string>;
    [key: string]: unknown;
}

interface ISwaggerUIConfig {
    spec: unknown;
    domNode: HTMLElement;
    deepLinking: boolean;
    presets: ReadonlyArray<unknown>;
    layout: string;
    requestInterceptor: (request: ISwaggerRequest) => ISwaggerRequest;
}

/**
 * The minimal shape of the global `SwaggerUIBundle` factory exposed by the
 * self-hosted `swagger-ui-bundle.js`. Declared locally (rather than depending
 * on `@types/swagger-ui-dist`) because only the call signature and the `apis`
 * preset are used.
 */
interface ISwaggerUIBundle {
    (config: ISwaggerUIConfig): void;
    presets: {
        apis: unknown;
    };
}

/**
 * Narrows the `unknown` global the vendor bundle installs. Checks the
 * `presets.apis` entry as well as callability, so the predicate verifies
 * everything {@link ISwaggerUIBundle} declares rather than asserting it.
 * @param value - The global to test.
 * @returns Whether the value is the Swagger-UI factory.
 */
function isSwaggerUIBundle (value: unknown): value is ISwaggerUIBundle {
    return typeof value === 'function' &&
        'presets' in value &&
        isRecord(value.presets) &&
        'apis' in value.presets;
}

type LoadStatus = 'loading' | 'ready' | 'error';

const swaggerStylesheetId = 'fm-swagger-ui-stylesheet';
const swaggerDarkStylesheetId = 'fm-swagger-ui-dark-stylesheet';
const apiDocsScrollerStylesheetId = 'fm-api-docs-scroller-stylesheet';
const swaggerScriptId = 'fm-swagger-ui-script';
const darkSchemeClass = 'fm-swagger-dark';
const colorSchemeChangeEvent = 'fm-color-scheme-change';

/**
 * Renders the OpenAPI documentation inside the SPA. The Swagger-UI assets are
 * self-hosted vendor files; the bundle is injected as a DOM-created
 * `<script>`, which CSP `'strict-dynamic'` trusts because the injecting code
 * is part of the already-trusted application bundle. The specification is
 * fetched with the existing JWT, so no cookie or extra login is involved, and
 * the same token is attached to every "Try it out" request.
 *
 * The component renders into the light DOM (`createRenderRoot` returns `this`);
 * `ensureStylesheet` injects the `.swagger-ui`-scoped vendor stylesheet so it
 * reaches the rendered tree, and `ensureStyle` injects two sheets of our own the
 * same way: the rules that make the page its own scroll container inside the
 * frame, and a dark override (`api_docs_dark_theme.ts`), the vendor stylesheet
 * being light-only; the page follows the app's System / Hell / Dunkel preference
 * via `applyColorScheme`.
 *
 * Inside the frame the page scrolls itself and says so with `data-scrolls-itself`,
 * which `<fm-application-frame>` answers by handing its scrollport over;
 * `tabindex="-1"` lets a click into the page take the focus the keys scroll by,
 * and `api_docs_scroller_styles.ts` has why the page has to be the scroller.
 *
 * The container carries two attributes the rest of the client does not need.
 * `lang="en"` because everything Swagger-UI renders into it is English inside a
 * German document, which is WCAG 3.1.2; `data-vendor-ui` because those contents
 * are the vendor's, which the accessibility sweep excludes while judging the
 * container itself. `docs/vendoring.md` → *Vendored UI* is that contract.
 */
@customElement('fm-api-docs')
export class FmApiDocs extends LitElement {

    @state()
    private status: LoadStatus = 'loading';

    protected override createRenderRoot (): HTMLElement {
        return this;
    }

    override connectedCallback (): void {
        super.connectedCallback();
        this.setAttribute('data-scrolls-itself', '');
        this.tabIndex = -1;
        this.ensureStylesheet();
        this.ensureStyle(apiDocsScrollerStylesheetId, apiDocsScrollerStyles);
        this.ensureStyle(swaggerDarkStylesheetId, swaggerDarkTheme);
    }

    protected override firstUpdated (): void {
        void this.initialize();
    }

    /**
     * Resolves where injected styles must live. The page is slotted into a
     * shell that lives in `<fm-app>`'s shadow root, and document-level styles
     * do not cross a shadow boundary; the enclosing shadow root (the element's
     * `getRootNode()`) is what reaches the rendered `.swagger-ui` tree. Falls
     * back to `document.head` outside a shadow root.
     * @returns The shadow root hosting the component, or the document head.
     */
    private resolveStyleTarget (): ShadowRoot | HTMLHeadElement {
        const root = this.getRootNode();

        return root instanceof ShadowRoot ? root : document.head;
    }

    private ensureStylesheet (): void {
        const target = this.resolveStyleTarget();

        if (target.querySelector(`#${swaggerStylesheetId}`) !== null) {
            return;
        }

        const link = document.createElement('link');
        link.id = swaggerStylesheetId;
        link.rel = 'stylesheet';
        link.href = `${runtimeConfig.swaggerUiBase}/swagger-ui.css`;
        target.appendChild(link);
    }

    private ensureStyle (id: string, styles: CSSResult): void {
        const target = this.resolveStyleTarget();

        if (target.querySelector(`#${id}`) !== null) {
            return;
        }

        const style = document.createElement('style');
        style.id = id;
        style.textContent = styles.cssText;
        target.appendChild(style);
    }

    private readonly windowEvents = new WindowEventsController(this, [
        onWindowEvent(colorSchemeChangeEvent, () => {
            this.applyColorScheme();
        }),
    ]);

    /**
     * Toggles the dark marker class on the Swagger-UI container from the
     * resolved scheme. The dark override is gated on this class because a
     * stylesheet inside the shell shadow root cannot select the `wa-dark`
     * class that `theme.ts` sets on `<html>` outside the boundary.
     */
    private applyColorScheme (): void {
        const container = this.querySelector('#swagger-ui');
        container?.classList.toggle(darkSchemeClass, getResolvedScheme() === 'dark');
    }

    private async initialize (): Promise<void> {
        try {
            const [
                spec,
                bundle,
            ] = await Promise.all([
                this.fetchSpec(),
                this.loadBundle(),
            ]);

            const container = this.querySelector<HTMLElement>('#swagger-ui');

            if (container === null) {
                throw new Error('Swagger-UI-Container nicht gefunden.');
            }

            bundle({
                spec,
                domNode: container,
                deepLinking: true,
                presets: [
                    bundle.presets.apis,
                ],
                layout: 'BaseLayout',
                requestInterceptor: this.requestInterceptor,
            });

            this.applyColorScheme();
            this.status = 'ready';
        } catch {
            this.status = 'error';
        }
    }

    private async fetchSpec (): Promise<unknown> {
        return valueOrThrow(await getOpenApiSpec(getAccessToken() === null));
    }

    /**
     * Reads the global Swagger-UI factory the injected bundle defines. The
     * `window.SwaggerUIBundle` global is typed `unknown` (see `types/fm.d.ts`),
     * so it is narrowed by {@link isSwaggerUIBundle}.
     * @returns The factory, or `undefined` when the bundle has not loaded yet.
     */
    private readBundle (): ISwaggerUIBundle | undefined {
        const candidate = window.SwaggerUIBundle;

        return isSwaggerUIBundle(candidate) ? candidate : undefined;
    }

    private async loadBundle (): Promise<ISwaggerUIBundle> {
        const loaded = this.readBundle();

        if (loaded !== undefined) {
            return loaded;
        }

        return new Promise<ISwaggerUIBundle>((resolve, reject) => {
            const script = document.createElement('script');
            script.id = swaggerScriptId;
            script.src = `${runtimeConfig.swaggerUiBase}/swagger-ui-bundle.js`;

            script.addEventListener('load', () => {
                const ready = this.readBundle();

                if (ready !== undefined) {
                    resolve(ready);
                } else {
                    reject(new Error('SwaggerUIBundle wurde nicht bereitgestellt.'));
                }
            });
            script.addEventListener('error', () => reject(new Error(loadFailed('Swagger-UI'))));

            document.head.appendChild(script);
        });
    }

    private readonly requestInterceptor = (request: ISwaggerRequest): ISwaggerRequest => {
        const token = getAccessToken();

        if (token !== null) {
            request.headers.Authorization = `Bearer ${token}`;
        }

        return request;
    };

    render (): TemplateResult {
        return html`
            ${this.status === 'error' ? html`
                <wa-callout variant="danger">
                    ${loadFailed('Die API-Dokumentation')} ${reloadHint}
                </wa-callout>
            ` : nothing}
            ${this.status === 'loading' ? html`
                <wa-spinner></wa-spinner>
            ` : nothing}
            <div id="swagger-ui" lang="en" data-vendor-ui="swagger-ui"></div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-api-docs': FmApiDocs;
    }
}
